import React, { useEffect, useState } from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Grid, FormControl, FormControlLabel, Radio, RadioGroup, Hidden } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Banner from '@cpg-its/ads.containers.banner';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { radioGroupTableStyles } from '../../../../../global/constants/theme/styles/tables/radio-table';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { GrayBox } from '../../../../../global/components/layout/grey-section';
import { setCurrentNewHireEnrollmentStep, setWhoWillEnrollCriteria } from '../slice/new-hire-enrollment-slices';
import { EnrollmentOptions, MemberOrAdmin, memberScheduleHoursHighThreshold, NewHireEnrollmentStep } from '../models/new-hire-enrollment-models';
import { useNewHireEnrollmentOptions } from '../hooks/useNewHireEnrollmentOptions';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions, memberOrAdminKey, WhoWillEnrollContentModel } from '../models/who-will-enroll-models';
import { clearLocalStorageSelections, getWhoWillEnrollCriterias, storeLocalStorageSelections } from '../helpers/who-will-enroll-helpers';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ScheduledHours } from '../../employment/employment-model';
import { NewHireProps } from '../../../models/arc-epi-props';

interface Props {
    basePath: string
    contentModelHook:()=> WhoWillEnrollContentModel;
    clientIdHook: any;
    participantEnrollment?: boolean;
}

function WhoWillEnroll(props: Props) {

    const { basePath, clientIdHook, contentModelHook, participantEnrollment } = props;
    clearLocalStorageSelections();
    const buttonClasses = buttonStyles();
    const tableClasses = radioGroupTableStyles();
    const contentModel = contentModelHook();
    const gpsItem = useGpsType(contentModel?.gpsType);
    const employerId = clientIdHook();
    const options = useSelector<TcsmStates, EnrollmentOptions>(state => state?.newHireEnrollmentState?.enrollmentOptions);
    const employmentId = useSelector<TcsmStates, number>(state => state.employmentState?.currentEmployment?.id);
    const scheduledHour = useSelector<TcsmStates, ScheduledHours>(state => state.employmentState?.currentEmployment?.scheduled_hours[0]);
    const below1000 = Number.isInteger(scheduledHour?.hours) && scheduledHour?.hours < memberScheduleHoursHighThreshold;
    const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
    const loaded = useNewHireEnrollmentOptions(employerId, employmentId ? `?employment_id=${employmentId}` : null, participantEnrollment);
    const dispatch = useDispatch();
    const history = useHistory();
    const [memberOrAdmin, setMemberOrAdmin] = useState<WhoWillEnrollCriterias>(getWhoWillEnrollCriterias(options));

    useEffect(() => {
        dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.WHO_WILL_ENROLL));
        setMemberOrAdmin(getWhoWillEnrollCriterias(options));
    }, [options]);


    const onChange = (e, key: keyof WhoWillEnrollCriterias) => {
        memberOrAdmin[key] = e?.target?.value;
        const updated = { ...memberOrAdmin };
        setMemberOrAdmin(updated);
    };

    const backAction = () => {
        history.goBack();
    };

    const continueAction = () => {

        if (memberOrAdmin.memberOrAdmin === WhoWillEnrollOptions.admin) {
            Object.keys(memberOrAdmin)?.forEach((key: keyof WhoWillEnrollCriterias) => {
                if (key !== memberOrAdminKey) {
                    memberOrAdmin[key] = null;
                }
            });
        }

        dispatch(setWhoWillEnrollCriteria(memberOrAdmin));
        storeLocalStorageSelections(memberOrAdmin);
        history.push(`${basePath}${NewHireEnrollmentRoutes.BenefitEnrollment}${AccountInfoRoutes.PersonalInfoSegment}`);
    };

    if (!loaded) {
        return <LoadingCircle />;
    }

    const initialValues = (plan) => {
        if (!memberOrAdmin[plan]) {
            memberOrAdmin[plan] = WhoWillEnrollOptions.admin;
        }
    };

    function getRadioOption(label: string, plan: keyof WhoWillEnrollCriterias, memberAdminOption: MemberOrAdmin) {

        if (!memberAdminOption) {
            return null;
        }

        if (memberAdminOption?.admin && !memberAdminOption.member) {
            return (
                <ContentSpacer>
                    <GrayBox>
                        <p><strong>{label}</strong></p>
                        <p>{contentModel?.administratorOnlyLabel}</p>
                        <WysiwygWithReactLink content={contentModel?.administratorOnlyDescription} />
                    </GrayBox>
                </ContentSpacer>
            );
        }

        return (

            <RadioGroup className='radio-table-row' value={initialValues(plan)} defaultValue={WhoWillEnrollOptions.admin} onChange={(e) => onChange(e, plan)} >
                <legend className='radio-table-row-label h4-styled'>{label}</legend>
                <div className='radio-table-row-cell radio-table-row-cell-desktop-title'><p><strong>{label}</strong></p></div>
                <FormControlLabel
                    disabled={!memberAdminOption?.member || !memberAdminOption?.admin}
                    className='radio-table-row-cell '
                    value={WhoWillEnrollOptions.member}
                    control={<Radio />}
                    label={<span className='radio-table-input-label'>{contentModel?.memberOptionText}</span>}
                />
                <FormControlLabel
                    disabled={!memberAdminOption?.member || !memberAdminOption?.admin}
                    className='radio-table-row-cell '
                    value={WhoWillEnrollOptions.admin} control={<Radio />}
                    label={<span className='radio-table-input-label'>{contentModel?.adminOptionText}</span>}
                />
            </RadioGroup>
        );

    }

    return (
        <PageContainer>
            <MainContent>
                {
                    contentModel?.initialSuccessMessage &&
                    <ContentSpacer>
                        <Banner banner={{
                            type: 'success',
                            label: <WysiwygWithReactLink content={contentModel?.initialSuccessMessage} />,
                        }} />
                    </ContentSpacer>
                }
                <PageHeader
                    headerText={contentModel?.whoWillEnrollPageTitle}
                    description={contentModel?.whoWillEnrollPageSubTitle}
                />
                <Hidden mdUp>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                    </SideBarContent>
                </Hidden>
                <DividerSection removeDivider>
                    <WysiwygWithReactLink content={contentModel?.whoWillEnrollPageDescription} />
                    {
                        below1000 && <WysiwygWithReactLink content={newHireProps?.lessThan1000HourNoGhlMessage} />
                    }
                </DividerSection>
                <DividerSection removeDivider>
                    <FormControl component='fieldset'>
                        <p><strong>{contentModel?.enrollmentTypeLabel}</strong></p>
                        <RadioGroup value={memberOrAdmin.memberOrAdmin} onChange={(e) => onChange(e, 'memberOrAdmin')} >
                            <FormControlLabel className='input-label' value={WhoWillEnrollOptions.member} control={<Radio disabled={below1000} />} label={<>{contentModel?.memberSelfEnrollLabel}</>} />
                            {
                                memberOrAdmin.memberOrAdmin === WhoWillEnrollOptions.member &&
                                <ContentSpacer>
                                    <ContentSpacer>
                                        <WysiwygWithReactLink content={contentModel?.memberSelfEnrollHelperTexts} />
                                    </ContentSpacer>
                                    {
                                        !participantEnrollment &&
                                        <ContentSpacer>
                                            <GrayBox>
                                                <p><strong>{contentModel?.retirementPlanLabel}</strong></p>
                                                <p>{contentModel?.administratorOnlyLabel}</p>
                                                <WysiwygWithReactLink content={contentModel?.administratorOnlyDescription} />
                                            </GrayBox>
                                        </ContentSpacer>
                                    }
                                    <ContentSpacer>
                                        <div className={tableClasses.radioGroupTable}>
                                            <div className='radio-table-content'>
                                                {
                                                    getRadioOption(contentModel?.medicalPlanLabel, 'medical', options?.benefit_options?.medical)
                                                }
                                                {
                                                    getRadioOption(contentModel?.dentalPlanLabel, 'dental', options?.benefit_options?.dental)
                                                }
                                                {
                                                    getRadioOption(contentModel?.stdPlanLabel, 'std', options?.benefit_options?.std)
                                                }
                                                {
                                                    getRadioOption(contentModel?.ltdPlanLabel, 'ltd', options?.benefit_options?.ltd)
                                                }
                                            </div>
                                        </div>
                                    </ContentSpacer>
                                    <ContentSpacer>
                                        <WysiwygWithReactLink content={contentModel?.memberSelfEnrollAddtionalHelperTexts} />
                                    </ContentSpacer>
                                </ContentSpacer>
                            }

                            <FormControlLabel className='input-label' value={WhoWillEnrollOptions.admin} control={<Radio />} label={<>{contentModel?.adminEnrollBehalfOfMemberLabel}</>} />
                        </RadioGroup>
                    </FormControl>
                </DividerSection>
                <ButtonWrapper flexEnd>
                    {!contentModel?.hideBack && <button className={buttonClasses.tertiaryButton} type='button' onClick={backAction} >{contentModel?.goBackButtonText}</button>}
                    <button className={`${buttonClasses.primaryButton} ${memberOrAdmin.memberOrAdmin || 'disabled'}`} type='button' onClick={continueAction} >{contentModel?.continueButtonText}</button>
                </ButtonWrapper>
                <Grid container direction="row" justifyContent="flex-end"><Grid
                    justifyContent="flex-end"
                    container
                    item md={6}><p>{contentModel?.continueHelperText}</p>
                </Grid></Grid>
            </MainContent>
            <Hidden smDown>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
        </PageContainer>
    );
}

WhoWillEnroll.defaultProps = {
    participantEnrollment: false,
};

export default WhoWillEnroll;

